import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Progress } from '@/shared/components/ui/progress';
import MobileScanInput from '../components/MobileScanInput';
import {
  MapPin,
  Package,
  CheckCircle2,
  ChevronRight,
  ArrowRight,
  RotateCcw,
  Navigation
} from 'lucide-react';
import { toast } from 'sonner';
import { useMobile } from '../context/MobileContext';
import { wmsEvents } from '@/shared/services/trailService';
import { receivingAPI } from '@/shared/services/api';

const STEPS = {
  SELECT_TASK: 'select_task',
  SCAN_PALLET: 'scan_pallet',
  SCAN_LOCATION: 'scan_location',
  CONFIRM: 'confirm',
  COMPLETE: 'complete'
};

const MobilePutaway = () => {
  const navigate = useNavigate();
  const { setCurrentTask } = useMobile();
  const [currentStep, setCurrentStep] = useState(STEPS.SELECT_TASK);
  const [selectedTask, setSelectedTask] = useState(null);
  const [palletScanned, setPalletScanned] = useState(false);
  const [completedTasks, setCompletedTasks] = useState([]);

  // Dynamic state for put-away tasks
  const [tasks, setTasks] = useState([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);

  useEffect(() => {
    setCurrentTask({
      name: 'Put-away',
      href: '/mobile/putaway',
      description: 'Scan pallets to bin locations'
    });
    fetchPutawayTasks();
    return () => setCurrentTask(null);
  }, [setCurrentTask]);

  const fetchPutawayTasks = async () => {
    setIsLoadingTasks(true);
    try {
      const data = await receivingAPI.getPutawayTasks({ status: 'pending' });
      setTasks(Array.isArray(data) ? data : data.tasks || []);
    } catch (err) {
      toast.error('Failed to fetch put-away tasks');
    } finally {
      setIsLoadingTasks(false);
    }
  };

  const handleSelectTask = async (task) => {
    const userId = (JSON.parse(localStorage.getItem('ezerpUser') || '{}')?.id || '') || 'operator';

    try {
      await receivingAPI.startPutaway(task.id);
      setSelectedTask(task);
      setCurrentStep(STEPS.SCAN_PALLET);

      // Emit trail event for task started
      wmsEvents.putawayTaskStarted(task.id, userId);
    } catch (err) {
      toast.error(err.message || 'Failed to start put-away task');
    }
  };

  const handlePalletScanSuccess = (barcode) => {
    const userId = (JSON.parse(localStorage.getItem('ezerpUser') || '{}')?.id || '') || 'operator';

    if (barcode === selectedTask.palletId || barcode.includes(selectedTask.palletId)) {
      setPalletScanned(true);
      setCurrentStep(STEPS.SCAN_LOCATION);
      toast.success('Pallet Verified', { description: `Now scan bin ${selectedTask.suggestedBin}` });

      // Emit trail event for pallet scanned
      wmsEvents.putawayPalletScanned(selectedTask.id, selectedTask.palletId, userId);
    }
  };

  const handleLocationScanSuccess = (barcode) => {
    const userId = (JSON.parse(localStorage.getItem('ezerpUser') || '{}')?.id || '') || 'operator';

    // Check if scanned location matches suggested or is a valid alternative
    if (barcode === selectedTask.suggestedBin || barcode.match(/^[A-Z]-\d{2}-\d{2}-\d{2}$/)) {
      // Emit trail event for location scanned
      wmsEvents.putawayLocationScanned(selectedTask.id, barcode, userId);
      setCurrentStep(STEPS.CONFIRM);
    }
  };

  const handleConfirm = async () => {
    const userId = (JSON.parse(localStorage.getItem('ezerpUser') || '{}')?.id || '') || 'operator';

    try {
      await receivingAPI.confirmPutaway({
        task_id: selectedTask.id,
        actual_location: selectedTask.suggestedBin // In real use, this would be the scanned barcode
      });

      setCompletedTasks(prev => [...prev, selectedTask.id]);
      setTasks(prev => prev.filter(t => t.id !== selectedTask.id));

      // Emit trail event for put-away completed
      wmsEvents.putawayCompleted(selectedTask.id, selectedTask.suggestedBin, userId);

      setCurrentStep(STEPS.COMPLETE);
      toast.success('Put-away Complete', { description: `${selectedTask.palletId} stored in ${selectedTask.suggestedBin}` });
    } catch (err) {
      toast.error(err.message || 'Put-away confirmation failed');
    }
  };

  const startNextTask = () => {
    setCurrentStep(STEPS.SELECT_TASK);
    setSelectedTask(null);
    setPalletScanned(false);
  };

  const renderStep = () => {
    switch (currentStep) {
      case STEPS.SELECT_TASK:
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Put-away Tasks</h2>
              <Badge>{tasks.length} pending</Badge>
            </div>

            {tasks.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-4" />
                  <h3 className="font-semibold text-lg">All Tasks Complete!</h3>
                  <p className="text-muted-foreground">No pending put-away tasks</p>
                </CardContent>
              </Card>
            ) : (
              tasks.map((task) => (
                <Card
                  key={task.id}
                  className="cursor-pointer hover:shadow-md active:scale-[0.98] transition-all"
                  onClick={() => handleSelectTask(task)}
                  data-testid={`task-${task.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-xl text-white ${task.priority === 'high' ? 'bg-red-500' :
                        task.priority === 'low' ? 'bg-gray-400' : 'bg-green-500'
                        }`}>
                        <Package className="h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{task.palletId}</h3>
                          {task.priority === 'high' && <Badge variant="destructive">Urgent</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground">{task.cartons} carton(s)</p>
                        <div className="flex items-center gap-2 mt-1 text-sm">
                          <MapPin className="h-3 w-3 text-blue-500" />
                          <span className="font-mono">{task.suggestedBin}</span>
                          <Badge variant="outline" className="text-xs">{task.zone}</Badge>
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              ))
            )}

            {completedTasks.length > 0 && (
              <div className="text-center text-sm text-muted-foreground">
                {completedTasks.length} task(s) completed this session
              </div>
            )}
          </div>
        );

      case STEPS.SCAN_PALLET:
        return (
          <div className="space-y-4">
            {/* Task Info */}
            <Card className="bg-green-50 dark:bg-green-950/20 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Package className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="font-semibold">{selectedTask.palletId}</p>
                      <p className="text-sm text-muted-foreground">{selectedTask.cartons} carton(s)</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Put to</p>
                    <p className="font-mono font-semibold">{selectedTask.suggestedBin}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Progress Steps */}
            <div className="flex items-center gap-2 justify-center">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">1</div>
                <span className="text-sm font-medium">Scan Pallet</span>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <div className="flex items-center gap-2 opacity-50">
                <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-sm">2</div>
                <span className="text-sm">Scan Bin</span>
              </div>
            </div>

            <MobileScanInput
              title="Step 1: Scan Pallet/Carton"
              subtitle={`Scan ${selectedTask.palletId} to verify`}
              gateId="GATE_PUT_PALLET"
              barcodeType="PALLET"
              scanContext="PUTAWAY"
              expectedValue={selectedTask.palletId}
              onScanSuccess={handlePalletScanSuccess}
              validateScan={async (barcode) => ({
                success: barcode === selectedTask.palletId || barcode.includes(selectedTask.palletId),
                result: barcode === selectedTask.palletId ? 'OK' : 'REJECTED',
                message: barcode !== selectedTask.palletId ? 'Wrong pallet scanned' : null
              })}
            />

            <Button variant="outline" onClick={startNextTask} className="w-full">
              Cancel
            </Button>
          </div>
        );

      case STEPS.SCAN_LOCATION:
        return (
          <div className="space-y-4">
            {/* Task Info */}
            <Card className="bg-green-50 dark:bg-green-950/20 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="font-semibold">{selectedTask.palletId}</p>
                      <p className="text-xs text-green-600">Pallet verified</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Put to</p>
                    <p className="font-mono font-semibold text-lg">{selectedTask.suggestedBin}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Navigation Hint */}
            <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950/20">
              <CardContent className="p-4 flex items-center gap-3">
                <Navigation className="h-6 w-6 text-blue-600" />
                <div>
                  <p className="font-medium">Navigate to {selectedTask.zone}</p>
                  <p className="text-sm text-muted-foreground">Bin location: {selectedTask.suggestedBin}</p>
                </div>
              </CardContent>
            </Card>

            {/* Progress Steps */}
            <div className="flex items-center gap-2 justify-center">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm">
                  <CheckCircle2 className="h-4 w-4" />
                </div>
                <span className="text-sm text-green-600">Pallet</span>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">2</div>
                <span className="text-sm font-medium">Scan Bin</span>
              </div>
            </div>

            <MobileScanInput
              title="Step 2: Scan Bin Location"
              subtitle={`Scan bin ${selectedTask.suggestedBin} or alternative`}
              gateId="GATE_PUT_BIN"
              barcodeType="LOCATION"
              scanContext="PUTAWAY"
              expectedValue={selectedTask.suggestedBin}
              onScanSuccess={handleLocationScanSuccess}
              chainMapping={{ pallet_id: selectedTask.palletId }}
              validateScan={async (barcode) => {
                const isValidFormat = /^[A-Z]-\d{2}-\d{2}-\d{2}$/.test(barcode);
                const isExpected = barcode === selectedTask.suggestedBin;
                return {
                  success: isValidFormat,
                  result: isExpected ? 'OK' : (isValidFormat ? 'WARNING' : 'REJECTED'),
                  message: !isValidFormat ? 'Invalid bin format' : (!isExpected ? 'Different bin - confirm to proceed' : null)
                };
              }}
            />

            <Button variant="outline" onClick={() => setCurrentStep(STEPS.SCAN_PALLET)} className="w-full">
              Back
            </Button>
          </div>
        );

      case STEPS.CONFIRM:
        return (
          <div className="space-y-6 py-4">
            <Card className="border-2 border-primary">
              <CardContent className="p-6 text-center">
                <h2 className="text-xl font-bold mb-4">Confirm Put-away</h2>

                <div className="space-y-4">
                  <div className="flex items-center justify-center gap-4">
                    <div className="text-center">
                      <Package className="h-8 w-8 mx-auto text-blue-500 mb-2" />
                      <p className="font-mono font-semibold">{selectedTask.palletId}</p>
                      <p className="text-xs text-muted-foreground">{selectedTask.cartons} carton(s)</p>
                    </div>
                    <ArrowRight className="h-6 w-6 text-muted-foreground" />
                    <div className="text-center">
                      <MapPin className="h-8 w-8 mx-auto text-green-500 mb-2" />
                      <p className="font-mono font-semibold">{selectedTask.suggestedBin}</p>
                      <p className="text-xs text-muted-foreground">{selectedTask.zone}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setCurrentStep(STEPS.SCAN_LOCATION)} className="flex-1">
                Back
              </Button>
              <Button onClick={handleConfirm} className="flex-1">
                Confirm Put-away
              </Button>
            </div>
          </div>
        );

      case STEPS.COMPLETE:
        return (
          <div className="space-y-6 text-center py-8">
            <div className="mx-auto w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Put-away Complete!</h2>
              <p className="text-muted-foreground mt-2">
                {selectedTask.palletId} → {selectedTask.suggestedBin}
              </p>
            </div>
            <div className="flex gap-3 max-w-sm mx-auto">
              <Button variant="outline" onClick={startNextTask} className="flex-1">
                <RotateCcw className="h-4 w-4 mr-2" />
                Next Task
              </Button>
              <Button onClick={() => navigate('/mobile')} className="flex-1">
                Done
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="p-4" data-testid="mobile-putaway">
      {renderStep()}
    </div>
  );
};

export default MobilePutaway;
